"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

exports.__esModule = true;
exports["default"] = exports.generateTransparency = exports.parseColour = void 0;

var _extends5 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));

var _polished = require("polished");

/**
 * Extracts the opacity from an RGBA colour string.
 * @param {string} colour An RGBA colour string
 */
var parseColour = function parseColour(colour) {
  var splitColour = colour.match(/\(.*?\)/).join().replace(/(\(|\))/g, "").split(",").map(function (value) {
    return Number(value);
  });
  return splitColour;
};
/**
 * Reduces the opacity value of a colour down to a percentage of its
 * current value.
 * @param {array} colour An RGBA colour value, as an array.
 * @see parseColour parseColour can be used to convert an RGBA string to an array.
 * @param {number} percentage The percentage value to reduce the opacity to. Defaults
 * to 12% of the current value.
 */


exports.parseColour = parseColour;

var generateTransparency = function generateTransparency(_ref, percentage) {
  var r = _ref[0],
      g = _ref[1],
      b = _ref[2],
      a = _ref[3];

  if (percentage === void 0) {
    percentage = 0.12;
  }

  var targetOpacity = a * percentage;
  return (0, _polished.rgba)(r, g, b, targetOpacity);
};

exports.generateTransparency = generateTransparency;

var isDark = function isDark(colour) {
  return colour === "#000";
};

var readableThemeColour = function readableThemeColour(colour) {
  return isDark((0, _polished.readableColor)(colour)) ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.87)";
};
/**
 * Generates CSS for the button component.
 * @param {string} colour The colour of the button, in RGBA CSS format.
 */


var createButtonVariant = function createButtonVariant(colour) {
  var hoverColour = (0, _polished.darken)(0.12)(colour);
  var activeColour = (0, _polished.darken)(0.16)(colour);
  return {
    color: readableThemeColour(colour),
    backgroundColor: colour,
    transition: "0.18s",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: hoverColour
    },
    "&:active": {
      backgroundColor: activeColour
    },
    "&:focus": {
      backgroundColor: activeColour
    },
    "&:disabled": {
      opacity: 0.25,
      cursor: "not-allowed"
    }
  };
};
/**
 * Generates CSS for the "outline" style variation of the button component.
 * @param {string} colour The colour of the button, in RGBA CSS format.
 */


var createOutlineButtonVariant = function createOutlineButtonVariant(colour) {
  var hoverColour = (0, _polished.darken)(0.12)(colour);
  var activeColour = (0, _polished.darken)(0.16)(colour);
  return {
    color: colour,
    backgroundColor: "transparent",
    border: "2px solid " + colour,
    transition: "0.18s",
    fontWeight: 500,
    borderRadius: "4px",
    "&:hover": {
      color: hoverColour,
      border: "2px solid " + hoverColour
    },
    "&:active": {
      color: activeColour,
      border: "2px solid " + activeColour
    },
    "&:focus": {
      color: activeColour,
      border: "2px solid " + activeColour
    },
    "&:disabled": {
      opacity: 0.25,
      cursor: "not-allowed"
    }
  };
};
/**
 * Generates CSS for the "text" style variation of the button component.
 * @param {string} colour The colour of the button, in RGBA CSS format.
 */


var createTextButtonVariant = function createTextButtonVariant(colour) {
  var parsedColour = parseColour(colour);
  return {
    color: colour,
    backgroundColor: "transparent",
    fontWeight: 500,
    transition: "0.18s",
    "&:hover": {
      backgroundColor: generateTransparency(parsedColour),
      cursor: "pointer"
    },
    "&:focus": {
      backgroundColor: generateTransparency(parsedColour)
    },
    "&:active": {
      backgroundColor: generateTransparency(parsedColour, 0.24)
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.54)",
      cursor: "not-allowed",
      opacity: 0.54,
      "&:hover": {
        backgroundColor: "transparent"
      },
      "&:active": {
        backgroundColor: "transparent"
      }
    }
  };
};
/**
 * Maps an array of colours to CSS for the button component. Used when there's multiple
 * colours under a single key in the theme (e.g. dark: ["rgb(0, 0, 0)", "rgb(47, 79, 79)", "rgb(112, 128, 144)"])
 * @param buttonVariantMap The accumulated list of styles for button components.
 * @param colours All available colours from the theme.
 * @param colour The current theme colour to generate a style for.
 */


var mapPaletteToButtonStyle = function mapPaletteToButtonStyle(buttonVariantMap, colours, colour, styleCreator) {
  var _extends3;

  return (0, _extends5["default"])({}, buttonVariantMap, (_extends3 = {}, _extends3[colour] = colours[colour].reduce(function (paletteMap, shadeColour, index) {
    var _extends2;

    return (0, _extends5["default"])({}, paletteMap, (_extends2 = {}, _extends2[index] = styleCreator(shadeColour), _extends2));
  }, {}), _extends3));
};
/**
 * Maps a colour to CSS for the button component.
 * @param buttonVariantMap The accumulated list of styles for button components.
 * @param colours All available colours from the theme.
 * @param colour The current theme colour to generate a style for.
 */


var mapColourToButtonStyle = function mapColourToButtonStyle(buttonVariantMap, colours, colour, styleCreator) {
  var _extends4;

  return (0, _extends5["default"])({}, buttonVariantMap, (_extends4 = {}, _extends4[colour] = styleCreator(colours[colour]), _extends4));
};
/**
 * Creates an array of style objects for all of the available theme colours. Can be optionally
 * supplied a function to create "variations" e.g. a button with a transparent background, and
 * coloured borders.
 * @param {string[]} colours A list of colours to generate button styles for
 * @param {Function} styleCreator A function for generating button styles. Defaults to the
 * function used to create buttons with a solid background.
 */


var generateStyles = function generateStyles(colours, styleCreator) {
  if (styleCreator === void 0) {
    styleCreator = createButtonVariant;
  }

  return Object.keys(colours).reduce(function (buttonVariantMap, colour) {
    if (Array.isArray(colours[colour])) {
      return mapPaletteToButtonStyle(buttonVariantMap, colours, colour, styleCreator);
    }

    return mapColourToButtonStyle(buttonVariantMap, colours, colour, styleCreator);
  }, {});
};
/**
 * Creates button-specific CSS corresponding with all available theme colours.
 * @param colours An object of colours, with keys for their name within the theme, and values
 * set as RGBA CSS strings.
 */


var generateButtonStyles = function generateButtonStyles(colours) {
  return (0, _extends5["default"])({}, generateStyles(colours), {
    outline: (0, _extends5["default"])({}, generateStyles(colours, createOutlineButtonVariant)),
    text: (0, _extends5["default"])({}, generateStyles(colours, createTextButtonVariant))
  });
};

var _default = generateButtonStyles;
exports["default"] = _default;